import { useState } from "react";
import { mockNotifications } from "../mocks/notifications";



function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export default function NotificationsPanel({ onClose }) {
  const [notifications, setNotifications] = useState(mockNotifications);
  const [activeFilter, setActiveFilter] = useState("all");

  const unreadCount = notifications.filter(n => !n.read).length;

  const filtered = notifications.filter(n => {
    if (activeFilter === "unread") return !n.read;
    if (activeFilter === "all") return true;
    return n.type === activeFilter;
  });

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const filters = [
    { key: "all", label: "All" },
    { key: "unread", label: "Unread" },
    { key: "transaction", label: "Transactions" },
    { key: "alert", label: "Alerts" },
    { key: "security", label: "Security" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(15,76,53,0.18)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Panel slides up from bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#F7F8FA] rounded-t-3xl flex flex-col"
        style={{ maxHeight: "90vh" }}>

        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-200 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button onClick={markAllRead}
                className="text-emerald-600 text-xs font-semibold cursor-pointer hover:text-emerald-700 transition-colors whitespace-nowrap">
                Mark all read
              </button>
            )}
            <button onClick={onClose}
              className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors">
              <i className="ri-close-line text-slate-600 text-base"></i>
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 px-5 pb-4 overflow-x-auto">
          {filters.map(f => (
            <button key={f.key} onClick={() => setActiveFilter(f.key)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: activeFilter === f.key ? "#059669" : "#fff",
                color: activeFilter === f.key ? "#fff" : "#64748B",
                border: activeFilter === f.key ? "none" : "1px solid #E2E8F0",
              }}>
              {f.label}
              {f.key === "unread" && unreadCount > 0 && (
                <span className="ml-1 bg-white/30 text-white rounded-full px-1">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto px-5 pb-8 space-y-2">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
                <i className="ri-notification-off-line text-slate-300 text-3xl"></i>
              </div>
              <p className="text-slate-400 text-sm font-medium">No notifications here</p>
            </div>
          ) : (
            filtered.map(notif => (
              <button key={notif.id} onClick={() => markRead(notif.id)}
                className="w-full text-left bg-white rounded-2xl px-4 py-3.5 flex items-start gap-3 cursor-pointer hover:bg-slate-50 transition-colors border border-slate-100 relative">
                {/* Unread dot */}
                {!notif.read && (
                  <span className="absolute top-3.5 right-3.5 w-2 h-2 bg-emerald-500 rounded-full"></span>
                )}
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: notif.iconBg }}>
                  <i className={`${notif.icon} text-lg`} style={{ color: notif.iconColor }}></i>
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0 pr-4">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className={`text-sm font-bold truncate ${notif.read ? "text-slate-600" : "text-slate-800"}`}
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {notif.title}
                    </p>
                  </div>
                  <p className="text-slate-400 text-xs leading-relaxed line-clamp-2">{notif.message}</p>
                  <p className="text-slate-300 text-xs mt-1.5 font-medium">{timeAgo(notif.time)}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
